<template>
    <div ref="rootRef" class="relative" :class="value.length < 5 ? 'w-fit' : 'w-full'">
        <button
            type="button"
            @click="handleTriggerClick"
            class="group flex w-full min-w-0 items-center justify-between rounded-full border border-transparent bg-dark px-4 py-2 text-primary-lighter shadow-lg transition-all duration-200 hover:border-primary-medium hover:bg-primary-dark focus:ring-2 focus:ring-blue-300 focus:outline-none active:scale-95"
            :aria-expanded="isInlineMode ? isOpen : undefined"
            :aria-haspopup="isInlineMode ? 'listbox' : undefined"
        >
            <span class="flex min-w-0 flex-1 items-center gap-3">
                <img
                    v-if="iconSrc"
                    :src="iconSrc"
                    alt=""
                    class="h-6 w-6 shrink-0 object-contain opacity-95 transition-transform group-hover:scale-110"
                />
                <span v-else-if="icon" class="shrink-0 text-xl opacity-90 transition-transform group-hover:scale-110">
                    {{ icon }}
                </span>

                <span class="flex min-w-0 flex-1 items-baseline gap-2">
                    <span class="shrink-0 text-lg font-medium whitespace-nowrap text-primary-light italic">
                        {{ label }} <span v-if="required" class="text-red-600">*</span> :
                    </span>
                    <span
                        class="block truncate rounded-xl bg-primary-light p-1 px-3 text-lg font-bold tracking-wide text-dark"
                        :class="!isFull && value.length < 5 ? 'max-w-fit' : 'flex-1'"
                    >
                        {{ value }}
                    </span>
                </span>
            </span>

            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="ml-3 h-4 w-4 shrink-0 opacity-50 transition-all group-hover:opacity-100"
                :class="isInlineMode && isOpen ? 'rotate-180' : ''"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>

        <div
            v-if="isInlineMode && isOpen"
            class="absolute z-40 mt-2 w-full overflow-hidden rounded-2xl border border-primary-medium shadow-xl"
            role="listbox"
        >
            <button
                v-for="option in options"
                :key="option.value"
                type="button"
                :disabled="isOptionDisabled(option)"
                @click="selectOption(option)"
                class="w-full px-4 py-3 text-left text-sm font-medium transition"
                :class="[
                    modelValue === option.value
                        ? 'bg-dark text-primary-lighter'
                        : isOptionDisabled(option)
                          ? 'cursor-not-allowed bg-gray-400 text-primary-dark opacity-100'
                          : 'bg-primary-lighter text-dark hover:bg-primary-medium hover:text-primary-dark',
                ]"
            >
                {{ option.label }}
            </button>

            <template v-for="([groupName, groupOptions], groupIndex) in groupEntries" :key="`group-${groupIndex}`">
                <p v-if="groupName" class="bg-gray-50 px-4 py-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    {{ groupName }}
                </p>
                <button
                    v-for="option in groupOptions"
                    :key="`${groupName}-${option.value}`"
                    type="button"
                    :disabled="isOptionDisabled(option)"
                    @click="selectOption(option)"
                    class="w-full px-4 py-3 text-left text-sm font-medium transition"
                    :class="[
                        modelValue === option.value
                            ? 'bg-primary text-white'
                            : isOptionDisabled(option)
                              ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                              : 'bg-white text-dark hover:bg-primary-light hover:text-primary-dark',
                    ]"
                >
                    {{ option.label }}
                </button>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface Props {
    label: string;
    value: string;
    icon?: string;
    iconSrc?: string;
    required?: boolean;
    modelValue?: string;
    options?: SelectOption[];
    groupedOptions?: Record<string, SelectOption[]>;
    disabledValues?: string[];
    isFull?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    required: false,
    modelValue: '',
    options: () => [],
    groupedOptions: () => ({}),
    disabledValues: () => [],
    isFull: false,
});

const emit = defineEmits<{
    (e: 'open'): void;
    (e: 'update:modelValue', value: string): void;
    (e: 'select', value: string): void;
}>();

const rootRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const groupEntries = computed(() => Object.entries(props.groupedOptions));
const isInlineMode = computed(() => props.options.length > 0 || groupEntries.value.length > 0);

function isOptionDisabled(option: SelectOption) {
    return option.disabled || props.disabledValues.includes(option.value);
}

function handleTriggerClick() {
    if (!isInlineMode.value) {
        emit('open');
        return;
    }
    isOpen.value = !isOpen.value;
}

function selectOption(option: SelectOption) {
    if (isOptionDisabled(option)) return;
    emit('update:modelValue', option.value);
    emit('select', option.value);
    isOpen.value = false;
}

function onDocumentClick(event: MouseEvent) {
    if (!isInlineMode.value || !isOpen.value) return;
    const target = event.target as Node | null;
    if (rootRef.value && target && !rootRef.value.contains(target)) {
        isOpen.value = false;
    }
}

onMounted(() => {
    document.addEventListener('click', onDocumentClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick);
});
</script>
